import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

const BASE_URL = API_BASE_PATH;

// Auth headers helper
const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
    ...(isMultipart && { "Content-Type": "multipart/form-data" }),
  };
};

// Error handler
const showAxiosError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail || error?.message || fallback;
  toast.error(detail);
  console.error(detail);
};

// Helper to convert object to FormData
const toFormData = (data: Record<string, any>) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value instanceof File ? value : value.toString());
    }
  });
  return formData;
};

// Interfaces
export interface Product {
  product_id: string;
  product_name: string;
  product_price: number;
}

export interface User {
  id: string;
  name: string;
  phone_no?: string;
  email?: string;
}

export interface Branch {
  branch_city?: string;
  branch_id: string;
  branch_name: string;
  branch_address: string;
  branch_state: string;
  branch_zipcode: string;
  branch_country: string;
  gst_number: string;
}

export interface Contact {
  id: string;
  company_name: string;
  contact_person_name?: string;
  company_mail?: string;
  company_phone?: string;
  branch: Branch;
  users: User;
}

export interface Lead {
  lead_id: string;
  lead_description: string;
  products: Product;
  //users: User;
  no_logins: number;
  rate_per_login: number;
  contact: Contact;
}

export interface Address {
  branch_id: string;
  branch_name: string;
  branch_address: string;
  branch_city: string;
  branch_state: string;
  branch_zipcode: string;
  branch_country: string;
  gst_number: string;
}

export interface ProposalResponse {
  client_proposal_number: string;
  proposal_id: string;
  revision_id: string;
  quotation_id: string;
  quotaiton_id?: string;
  description: string;
  Date_of_proposal: string;
  validation_date: string;
  status: 'Draft' | 'Accepted' | 'Deleted' | 'Declined' | string;
  signature_name: string;
  currency: string;
  proposal_tax_percentage: number;
  proposal_discount_percentage: number;
  proposal_no_logins: number;
  proposal_rate_per_login: number;
  proposal_tax: number;
  proposal_amount: number;
  proposal_total_amount: number;
  proposal_discount: number;
  discount_amount: number;
  leads: Lead;
  company_name: string;
  contact_name: string;
  contact_persona_name: string;
  product_name: string;
  sub_total: number;
  address: Address[];
  created_at: string;
  updated_at: string;
  cgst: number;
  sgst: number;
  igst: number;
  cgst_percentage: number;
  sgst_percentage: number;
  igst_percentage: number;
  amc: number;
}

export interface ProposalWithHistoryResponse {
  proposal: {
    proposal_id: string;
    revision_id: string;
    quotation_id: string;
    Date_of_proposal: string;
    validation_date: string;
    lead_id: string;
    currency: string;
    tax: number | null;
    no_logins: number;
    rate_per_login: number;
    status: string;
    description: string;
    amount: number;
    total_amount: number;
    signature_name: string;
    Attachment: string | null;
    discount: number;
    client_id: string;
    proposal_discount_percentage: number;
    proposal_tax_percentage: number | null;
    cgst: number;
    sgst: number;
    igst: number;
    amc: number | null;
    discount_amount?: number;
  };
  history: any[];
}

// Fetch single proposal
export const fetchProposalById = async (proposal_id: string): Promise<ProposalResponse> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/proposal/${proposal_id}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch proposal");
    throw error;
  }
};

// Create proposal
export const createProposal = async (data: Record<string, any>): Promise<any> => {
  try {
    const formData = toFormData(data);
    const { data: resData } = await axios.post(`${BASE_URL}/proposal/create`, formData, {
      headers: getAuthHeaders(true),
    });
    toast.success("Proposal created successfully");
    return resData;
  } catch (error) {
    showAxiosError(error, "Failed to create proposal");
    throw error;
  }
};

// Fetch all proposals
export const fetchAllProposals = async (): Promise<ProposalResponse[]> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/get_all_proposal/`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch proposals");
    throw error;
  }
};

// Update proposal
export const updateProposal = async (proposal_id: string, data: Record<string, any>): Promise<ProposalResponse> => {
  try {
    const formData = toFormData(data);
    const { data: resData } = await axios.put(`${BASE_URL}/proposal/edit/${proposal_id}`, formData, {
      headers: getAuthHeaders(true),
    });
    toast.success("Proposal updated successfully");
    return resData;
  } catch (error) {
    showAxiosError(error, "Failed to update proposal");
    throw error;
  }
};

// Update proposal revision by proposal_id
export const updateProposalRevision = async (
  proposal_id: string,
  data: Record<string, any>
): Promise<ProposalResponse> => {
  try {
    const formData = toFormData(data); // convert object to multipart/form-data
    const { data: resData } = await axios.put(
      `${BASE_URL}/proposal/edit/revision/${proposal_id}`,
      formData,
      {
        headers: getAuthHeaders(true), // true => multipart/form-data
      }
    );
    toast.success("Proposal revision updated successfully");
    return resData;
  } catch (error) {
    showAxiosError(error, "Failed to update proposal revision");
    throw error;
  }
};


// Delete proposal
export const deleteProposal = async (proposal_id: string): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/proposal/${proposal_id}/delete`, {
      headers: getAuthHeaders(),
    });
    toast.success("Proposal deleted successfully");
  } catch (error) {
    showAxiosError(error, "Failed to delete proposal");
    throw error;
  }
};

// Fetch proposals by user ID
export const fetchProposalsByUserId = async (userId: string): Promise<ProposalResponse[]> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/proposal/get/${userId}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch proposals by user");
    throw error;
  }
};

export const deleteProposalsBulk = async (proposalIds: string[]): Promise<void> => {
  try {
    // Convert proposal IDs array into a comma-separated string
    const idsParam = proposalIds.join(",");

    // Perform DELETE request to bulk delete endpoint
    const response = await axios.delete(
      `${BASE_URL}/proposal-bulk-delete?proposal_ids=${encodeURIComponent(idsParam)}`,
      {
        headers: {
          ...getAuthHeaders(),
          accept: "application/json",
        },
      }
    );

    console.log("Bulk delete response:", response.data);

    // Show success toast from backend message
    toast.success(response.data?.message || "Proposals deleted successfully.");
  } catch (error) {
    showAxiosError(error, "Failed to delete proposals in bulk");
    throw error;
  }
};
// Fetch proposal with history
export const fetchProposalWithHistory = async (
  proposal_id: string,
  revision_id?: string,
  quotation_id?: string
): Promise<ProposalWithHistoryResponse> => {
  try {
    const params = new URLSearchParams();
    if (revision_id) params.append("revision_id", revision_id);
    if (quotation_id) params.append("quotation_id", quotation_id);
    
    const { data } = await axios.get(`${BASE_URL}/proposal/history/get/${proposal_id}`, {
      headers: getAuthHeaders(),
      params,
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch proposal with history");
    throw error;
  }
};

// Fetch proposal history method
export const fetchProposalHistoryMethod = async (proposal_id: string): Promise<any> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/proposal/history/method${proposal_id}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch proposal history method");
    throw error;
  }
};

// Fetch customized invoice
export const fetchCustomizedInvoice = async (proposal_id: string): Promise<any> => {
  try {
    const { data } = await axios.get(`${BASE_URL}/get/invoice/customized/${proposal_id}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch customized invoice");
    throw error;
  }
};